// Copyright (c) 2025 Audere, All Rights Reserved

import { useCallback, useEffect } from "react";
import {
  Button,
  Box,
  Divider,
  FormControl,
  FormLabel,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useForm, Controller } from "react-hook-form";
import { generatePath, useNavigate } from "react-router";
import { useSelector } from "react-redux";

import {
  type ProjectNameFormProps,
  type ProjectBasic,
  PROJECT_FLOW_QUERY_KEYS,
  PROJECT_FLOW_STEP_VALUES,
} from "../types/projects";
import { createStyleSheet } from "../utils/styles";
import { theme } from "../../theme";
import folderOpenUrl from "../../assets/folder_open.svg";
import { isValidString } from "../utils/string";
import ProjectItems from "./ProjectItems";
import { selectCurrentOrganizationId } from "../features/auth/authSlice";
import { clearSelectedTestIds, setProjectName } from "../features/data/projectSlice";
import { useAppDispatch } from "../features/store";
import { UPDATE_PROJECT_WITHOUT_TEST_PATH } from "../utils/constants";

const ProjectNameForm = (props: ProjectNameFormProps) => {
  const { projectData, handleStep, onCancel, listProjectsData } = props;
  const { t } = useTranslation("ProjectNameForm");
  const {
    control,
    handleSubmit,
    setValue,
    getValues,
    watch,
    formState: { isValid, errors },
  } = useForm<ProjectBasic>({
    defaultValues: projectData,
    mode: "onChange",
  });
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const currentOrganizationId = useSelector(selectCurrentOrganizationId);

  const onSubmit = useCallback(() => {
    handleStep(1, getValues());
    dispatch(clearSelectedTestIds());
  }, [handleStep, getValues, dispatch]);
  const handleProjectSelect = useCallback(
    (projectUid: string) => {
      const searchParams = new URLSearchParams(location.search);
      searchParams.set(PROJECT_FLOW_QUERY_KEYS.STEP, PROJECT_FLOW_STEP_VALUES.PROJECT_NAME);
      const path = generatePath(UPDATE_PROJECT_WITHOUT_TEST_PATH, {
        orgUid: currentOrganizationId,
        projectUid: projectUid,
      });
      navigate(`${path}?${searchParams.toString()}`);
    },
    [currentOrganizationId, navigate]
  );

  useEffect(() => {
    const subscription = watch(value => {
      const formValue = value?.projectName;
      if (formValue && formValue !== projectData?.projectName) {
        dispatch(setProjectName(formValue));
      }
    });
    return () => subscription.unsubscribe();
  }, [watch, dispatch, projectData?.projectName]);
  useEffect(() => {
    if (projectData.projectName) {
      setValue("projectName", projectData.projectName, { shouldValidate: true });
    }
  }, [projectData, setValue]);

  return (
    <Box sx={styles.container}>
      <Typography variant="h6" fontSize={40}>
        {t("projectNameHeader")}
      </Typography>
      <Box sx={styles.panelContainer}>
        <Stack sx={styles.panelHeader}>
          <img src={folderOpenUrl} style={{ height: 20, objectFit: "contain" }} alt="Folder Open" />
          <Stack>
            <Typography variant="body1bold">{t("projectTitle")}</Typography>
            <Typography variant="body2">{t("projectInfo")}</Typography>
          </Stack>
        </Stack>
        <Divider />
        <Box component="form" onSubmit={handleSubmit(onSubmit)} sx={styles.formContainer}>
          <Typography variant="h6">{t("projectNameTitle")}</Typography>
          <FormControl fullWidth sx={{ px: 0 }}>
            <FormLabel sx={{ mb: 1 }} htmlFor="projectName-input">
              {t("projectName")}{" "}
              <Typography component="span" color="error">
                *
              </Typography>
            </FormLabel>
            <Controller
              name="projectName"
              control={control}
              rules={{
                required: t("errorMessage"),
                validate: value => isValidString(value) || t("errorMessage"),
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  id="projectName-input"
                  variant="outlined"
                  error={!!errors.projectName}
                  helperText={errors.projectName?.message}
                  placeholder={t("projectNamePlaceholder")}
                  sx={{
                    "& .MuiOutlinedInput-input": {
                      paddingY: "12px",
                    },
                  }}
                />
              )}
            />
          </FormControl>
          <Box sx={styles.continueBtnContainer}>
            <Button variant="outlined" onClick={onCancel} sx={{ px: 5 }}>
              {t("common:cancel")}
            </Button>
            <Button
              type="submit"
              variant="contained"
              sx={{ px: 5 }}
              disabled={!isValid}
              tabIndex={1}
            >
              {t("continue")}
            </Button>
          </Box>
        </Box>
      </Box>
      {listProjectsData?.projects?.length && (
        <Box>
          <Typography variant="h6">{t("projectListItems.title")}</Typography>
          <Typography variant="body2" color="text.secondary">
            {t("projectListItems.subtitle")}
          </Typography>
          <ProjectItems
            projects={listProjectsData?.projects}
            onProjectSelect={handleProjectSelect}
          />
        </Box>
      )}
    </Box>
  );
};

export default ProjectNameForm;

const styles = createStyleSheet({
  container: {
    px: 2,
    display: "flex",
    flexDirection: "column",
    gap: 3,
  },
  panelContainer: {
    background: theme.palette.common.white,
    borderRadius: "6px",
  },
  panelHeader: {
    p: 1,
    px: 2,
    flexDirection: "row",
    justifyContent: "start",
    alignItems: "center",
    gap: 1,
  },
  formContainer: {
    display: "flex",
    flexDirection: "column",
    gap: 3,
    p: 2,
    px: 3,
  },
  continueBtnContainer: { display: "flex", justifyContent: "flex-end", gap: 2 },
});
